/**
 * Box recommendation (frontend mirror of backend/utils/boxRecommendation.js).
 *
 * The seller enters the ITEM dimensions; this returns the box we recommend
 * they use to mail it. Used for the live preview in the listing form. The
 * backend recomputes this authoritatively on save — keep the two in sync.
 *
 * All math is in inches / pounds.
 */

const PADDING_IN = 2;

const USPS_BOXES = [
  {
    name: 'USPS Priority Mail Small Flat Rate Box',
    dims: [8.625, 5.375, 1.625],
    source: 'Free at your Post Office or usps.com',
  },
  {
    name: 'USPS Priority Mail Medium Flat Rate Box',
    dims: [11, 8.5, 5.5],
    source: 'Free at your Post Office or usps.com',
  },
  {
    name: 'USPS Priority Mail Large Flat Rate Box',
    dims: [12, 12, 5.5],
    source: 'Free at your Post Office or usps.com',
  },
];

const GENERIC_BOXES = [
  { name: 'Small box (about 12×9×4 in)', dims: [12, 9, 4] },
  { name: 'Medium box (about 14×12×8 in)', dims: [14, 12, 8] },
  { name: 'Large box (about 18×14×12 in)', dims: [18, 14, 12] },
  { name: 'Extra-large box (about 24×18×16 in)', dims: [24, 18, 16] },
];

const sortDesc = (arr) => [...arr].sort((a, b) => b - a);

const fits = (itemDims, boxDims) => {
  const i = sortDesc(itemDims);
  const b = sortDesc(boxDims);
  return i[0] <= b[0] && i[1] <= b[1] && i[2] <= b[2];
};

const volume = (dims) => dims[0] * dims[1] * dims[2];

const isSoftGoods = (category) =>
  typeof category === 'string' && /cloth|shoe|apparel|wear|blanket|textile/i.test(category);

export function recommendBox(item = {}) {
  const length = parseFloat(item.length);
  const width = parseFloat(item.width);
  const height = parseFloat(item.height);
  const weight = parseFloat(item.weight) || 0;

  if (!length || !width || !height) return null;

  const padded = [length + PADDING_IN, width + PADDING_IN, height + PADDING_IN];
  const thin = sortDesc([length, width, height])[2] <= 3.5;

  if (isSoftGoods(item.category) && thin && weight <= 5) {
    return {
      name: 'USPS Priority Mail Padded Flat Rate Envelope',
      flatRate: true,
      source: 'Free at your Post Office or usps.com',
      note: 'Soft items like clothing fold flat — a padded flat-rate envelope is the cheapest way to ship them.',
    };
  }

  const uspsFit = USPS_BOXES.filter((b) => fits(padded, b.dims)).sort(
    (a, b) => volume(a.dims) - volume(b.dims)
  )[0];

  if (uspsFit) {
    return {
      name: uspsFit.name,
      flatRate: true,
      source: uspsFit.source,
      dims: uspsFit.dims,
      note: 'Flat Rate boxes are free and ship for one flat price regardless of weight (up to 70 lb).',
    };
  }

  const genericFit = GENERIC_BOXES.filter((b) => fits(padded, b.dims)).sort(
    (a, b) => volume(a.dims) - volume(b.dims)
  )[0];

  const round = (n) => Math.ceil(n * 10) / 10;

  if (genericFit) {
    return {
      name: genericFit.name,
      flatRate: false,
      source: 'Available at most shipping stores, or reuse a clean box this size.',
      dims: genericFit.dims,
      note: 'Leave about an inch of padding on each side so the item is protected.',
    };
  }

  return {
    name: `Any box at least ${round(padded[0])}×${round(padded[1])}×${round(padded[2])} in`,
    flatRate: false,
    source: 'Use any sturdy box at least this size, or reuse a clean one.',
    dims: padded.map(round),
    note: 'Leave about an inch of padding on each side so the item is protected.',
  };
}

export default recommendBox;
